// src/components/hotel/HotelGallery.jsx
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './css/HotelGallery.module.css';
import hotelImagePlaceholder from '../../../../assets/hoteldefault.avif';

const buildImageUrl = (image) => {
    if (!image) return hotelImagePlaceholder;
    const cleaned = String(image).replace(/\\/g, '/');
    if (cleaned.startsWith('http')) return cleaned;
    return `${cleaned}`;
};

/* Skeleton shimmer pendant le chargement d'une image */
const Img = ({ src, alt, className, ...rest }) => {
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);

    const handleError = (e) => {
        e.target.onerror = null;
        setFailed(true);
        setLoaded(true);
    };

    return (
        <div className={`${styles.imgWrap} ${loaded ? styles.imgLoaded : ''}`}>
            {!loaded && <span className={styles.imgSkeleton} aria-hidden="true" />}
            <img
                src={failed ? hotelImagePlaceholder : src}
                alt={alt}
                onLoad={() => setLoaded(true)}
                onError={handleError}
                className={className}
                {...rest}
            />
        </div>
    );
};

const HotelGallery = ({ images }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const list = images?.length ? images : null;
    const main = list ? buildImageUrl(list[0]) : hotelImagePlaceholder;
    // Vignettes : les 5 suivantes (ou toutes si moins)
    const thumbs = list ? list.slice(1, 6) : [];

    const openFullscreen = (index = 0) => {
        setActiveIndex(index);
        setIsFullscreen(true);
    };

    const closeFullscreen = () => setIsFullscreen(false);

    const next = () => setActiveIndex((i) => (list ? (i + 1) % list.length : 0));
    const prev = () => setActiveIndex((i) => (list ? (i - 1 + list.length) % list.length : 0));

    return (
        <>
            <div className={styles.hotelGallery}>
                {list ? (
                    <>
                        {/* Grande image principale */}
                        <button
                            className={styles.mainImageWrap}
                            onClick={() => openFullscreen(0)}
                            aria-label="Open photo gallery"
                        >
                            <Img src={main} alt="Hotel" className={styles.mainImage} />
                            <span className={styles.viewAllBtn}>
                                <i className="fa fa-images" /> View all {list.length} photos
                            </span>
                        </button>

                        {/* Grille de vignettes */}
                        {thumbs.length > 0 && (
                            <div className={styles.thumbGrid}>
                                {thumbs.map((image, index) => (
                                    <button
                                        key={index}
                                        className={styles.thumbWrap}
                                        onClick={() => openFullscreen(index + 1)}
                                        aria-label={`View photo ${index + 2}`}
                                    >
                                        <Img
                                            src={buildImageUrl(image)}
                                            alt={`Hotel view ${index + 2}`}
                                            className={styles.thumbImage}
                                            loading="lazy"
                                        />
                                        {index === thumbs.length - 1 && list.length > 6 && (
                                            <span className={styles.moreOverlay}>
                                                +{list.length - 6} photos
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <div className={styles.noImages}>
                        <Img src={hotelImagePlaceholder} alt="Default hotel view" className={styles.mainImage} />
                        <div className={styles.noImagesText}>No photos available</div>
                    </div>
                )}
            </div>

            {/* Lightbox plein écran */}
            {isFullscreen && list && createPortal(
                <div className={styles.fullscreenModal} onClick={closeFullscreen}>
                    <div className={styles.fullscreenContent} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.closeFullscreen} onClick={closeFullscreen} aria-label="Close">
                            <i className="fa fa-times" />
                        </button>

                        <button className={`${styles.fullscreenArrow} ${styles.fullscreenArrowLeft}`} onClick={prev} aria-label="Previous photo">
                            <i className="fa fa-chevron-left" />
                        </button>

                        <Img
                            src={buildImageUrl(list[activeIndex])}
                            alt={`Hotel view ${activeIndex + 1}`}
                            className={styles.fullscreenImage}
                        />

                        <button className={`${styles.fullscreenArrow} ${styles.fullscreenArrowRight}`} onClick={next} aria-label="Next photo">
                            <i className="fa fa-chevron-right" />
                        </button>

                        <div className={styles.fullscreenCounter}>
                            {activeIndex + 1} / {list.length}
                        </div>

                        <div className={styles.fullscreenThumbs}>
                            {list.map((image, index) => (
                                <button
                                    key={index}
                                    className={`${styles.fullscreenThumb} ${index === activeIndex ? styles.active : ''}`}
                                    onClick={() => setActiveIndex(index)}
                                    aria-label={`Photo ${index + 1}`}
                                >
                                    <Img src={buildImageUrl(image)} alt="" className={styles.fullscreenThumbImg} />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default HotelGallery;
